import {
    DataSource,
    IList,
    IListItem,
    IListItemState,
    ListEvent,
    ListModel as ListModel,
} from '@mintdata/mint-kit/api/models/List';
import {IDisposable} from '@mintdata/mint-kit/api/resources/IDisposable';
import {IStyles, Styles} from '@mintdata/mint-kit/impl/util/Styles';
import {bind} from 'bind-decorator';
import * as classnames from 'classnames/bind';
import get from 'lodash/get';
import * as React from 'react';

const listStyles = require('@mintdata/mint-kit/impl/components/list/ListView.less');
const cx = classnames.bind(listStyles);

export interface IListItemData<T = any>
{
    label?:string | JSX.Element;
    value?:T;
    tip?:string;
}

export type ListItemRenderer<T> = (data:T, state:IListItemState, styles:IStyles) => JSX.Element;
export type ClickItemHandler<T> = (data:T) => void;
export type SelectItemHandler<T> = (item:IListItem<T>, state:IListItemState) => void;

export type MouseEnterItemHandler<T> = (data:T, target:HTMLElement) => void;
export type MouseLeaveItemHandler<T> = (data:T, target:HTMLElement) => void;

export interface IOnItemHover<T>
{
    onItemMouseEnter:MouseEnterItemHandler<T>,
    onItemMouseLeave:MouseLeaveItemHandler<T>,
}

export interface IListProps<T>
{
    className?:string;
    dataSource:DataSource<T>;
    itemRenderer?:ListItemRenderer<T>;
    onItemClick?:ClickItemHandler<T>;
    onItemHover?:IOnItemHover<T>;
    onItemSelect?:SelectItemHandler<T>;
    theme?:IStyles;
    selectOnItemHover?:boolean;
}

export interface IListState<T>
{
    listModel?:IList<T>;
    styles?:IStyles;
}

export class ListView<T> extends React.Component <IListProps<T>, IListState<T>>
{
    private _listEventListenerDisposer:IDisposable = null;
    private _isMounted:boolean = false;

    constructor(props:any)
    {
        super(props);

        this.state = {
            listModel: null,
            styles: null,
        };
    }

    @bind
    private _onItemRef(listItemRef:HTMLElement, listItem:IListItem<T>):void
    {
        // scroll view to selected item
        if( listItemRef && listItem.getState().isSelected )
        {
            requestAnimationFrame(() =>
            {
                listItemRef.scrollIntoView({block: 'nearest', behavior: 'auto'});
            });
        }
    }

    @bind
    private _onItemClick(listItem:IListItem<T>):void
    {
        //Implement selection strategies.
        const {listModel} = this.state;
        const {onItemSelect} = this.props;

        if( listItem.isDisabled() )
            return;

        listModel.unselectAll();
        listItem.select();

        if( onItemSelect )
            onItemSelect(listItem, listItem.getState());

        const {onItemClick} = this.props;

        if( onItemClick )
            onItemClick(listItem.getData());

    }

    @bind
    private _onItemMouseEnter(listItem:IListItem<T>, target:HTMLElement):void
    {
        if( listItem.isDisabled() )
            return;

        const onItemMouseEnter = get(this.props, 'onItemHover.onItemMouseEnter') as MouseEnterItemHandler<T>;

        if( onItemMouseEnter )
            onItemMouseEnter(listItem.getData(), target);

        const {selectOnItemHover} = this.props;

        if( !selectOnItemHover ) return;

        const {listModel} = this.state;

        listModel.unselectAll();
        listItem.select();

        const {onItemSelect} = this.props;

        if( onItemSelect )
            onItemSelect(listItem, listItem.getState());
    }

    @bind
    private _onItemMouseLeave(listItem:IListItem<T>, target:HTMLElement):void
    {
        if( listItem.isDisabled() )
            return;

        const onItemMouseLeave = get(this.props, 'onItemHover.onItemMouseLeave') as MouseLeaveItemHandler<T>;

        if( onItemMouseLeave )
            onItemMouseLeave(listItem.getData(), target);
    }

    @bind
    private onListEvent(listEvent:ListEvent<T>):void
    {
        if( this._isMounted )
            this.forceUpdate();
    }

    private _initDataSource(dataSource:DataSource<T>):void
    {
        if( this._listEventListenerDisposer )
        {
            this._listEventListenerDisposer.dispose();
            this._listEventListenerDisposer = null;
        }

        const listModel:IList<T> = dataSource instanceof ListModel
            ? dataSource
            : new ListModel<T>(dataSource as Array<T>);

        listModel.onListEvent(this.onListEvent);

        this.setState({
            listModel,
        });
    }

    private _initStyles(theme:IStyles):void
    {
        this.setState({
            styles: Styles.mergeStyles(theme, listStyles),
        });
    }

    public componentDidMount():void
    {
        const {dataSource, theme} = this.props;
        this._isMounted = true;

        this._initDataSource(dataSource);
        this._initStyles(theme);
    }

    componentWillUnmount():void
    {
        this._isMounted = false;
    }

    public componentWillReceiveProps(newProps:IListProps<T>):void
    {
        const {dataSource, theme} = this.props;

        if( dataSource !== newProps.dataSource )
            this._initDataSource(newProps.dataSource);

        if( theme !== newProps.theme )
            this._initStyles(newProps.theme);
    }

    @bind
    private _renderItem(item:IListItem<T>):JSX.Element
    {
        const {itemRenderer = ListView.renderItem} = this.props;
        const {styles} = this.state;
        const itemUid = item.getUid();

        return (
            <div
                key={itemUid}
                className={styles.itemContainer}
                ref={(ref) =>
                {
                    this._onItemRef(ref, item);
                }}
                onClick={() =>
                {
                    this._onItemClick(item);
                }}
                onMouseEnter={(event) =>
                {
                    this._onItemMouseEnter(item, event.target as HTMLElement);
                }}
                onMouseLeave={(event) =>
                {
                    this._onItemMouseLeave(item, event.target as HTMLElement);
                }}
            >
                {itemRenderer(item.getData(), item.getState(), styles)}
            </div>
        );
    }

    public render():JSX.Element
    {
        const {className = null} = this.props;
        const {listModel} = this.state;

        return listModel && <div className={cx('list', className)}>
            {
                listModel.getItems().map((item:IListItem<T>) =>
                {
                    return this._renderItem(item);
                })
            }
        </div>;
    }

    public static renderItem<T>(data:T, state:IListItemState, styles:IStyles):JSX.Element
    {
        const {label} = data as IListItemData;
        const cx = classnames.bind(styles);

        return <div className={cx('listItem', {isSelected: state.isSelected, isDisabled: state.isDisabled})}>
            {label}
        </div>;
    }
}

export type ListViewConstructor<T> = new() => ListView<T>;
